import type { CatalogLogo } from "@core-ui/ContextServices/CatalogLogoService/catalogLogoHooks";
import type ContextService from "@core-ui/ContextServices/ContextService";
import { type OnDidCommandEv, useApiEvent } from "@core-ui/hooks/useApi";
import BranchUpdaterService from "@ext/git/actions/Branch/BranchUpdaterService/logic/BranchUpdaterService";
import OnBranchUpdateCaller from "@ext/git/actions/Branch/BranchUpdaterService/model/OnBranchUpdateCaller";
import SyncService from "@ext/git/actions/Sync/logic/SyncService";
import { LRUCache } from "lru-cache";
import { type ReactElement, useEffect } from "react";

export enum ClientCacheZone {
	ViewRenderData = "view-render-data",
	CatalogLogo = "catalog-logo",
}

export interface ClientCacheLimits {
	viewRenderData: number;
	catalogLogo: number;
}

export interface ClientCacheTtls {
	viewRenderData: number;
	catalogLogo: number;
}

type CacheEntry = { state: "pending"; value: Promise<unknown> } | { state: "resolved"; value: unknown };

type ReleaseValue = (value: unknown) => void;

const DEFAULT_LIMITS: ClientCacheLimits = {
	viewRenderData: 100,
	catalogLogo: 1_000,
};

// A logo that expired would be loaded again by the next card to mount, and the home page remounts its cards on purpose.
const DEFAULT_TTLS: ClientCacheTtls = {
	viewRenderData: 5 * 60 * 1_000,
	catalogLogo: 0,
};

// A file logo is held as a blob URL, which the browser keeps alive until it is revoked.
const RELEASE_VALUE: Partial<Record<ClientCacheZone, ReleaseValue>> = {
	[ClientCacheZone.CatalogLogo]: (logo: CatalogLogo | null) => {
		if (logo && "src" in logo) URL.revokeObjectURL(logo.src);
	},
};

const createZone = (zone: ClientCacheZone, max: number, ttl: number): LRUCache<string, CacheEntry> => {
	const release = RELEASE_VALUE[zone];
	return new LRUCache({
		max,
		ttl,
		dispose: release && ((entry) => entry.state === "resolved" && release(entry.value)),
	});
};

const INVALIDATING_COMMANDS = new Set([
	"article/create",
	"article/features/setContent",
	"article/move",
	"article/property/move",
	"article/property/remove",
	"article/property/set",
	"article/property/update",
	"catalog/language/add",
	"catalog/language/remove",
	"catalog/move",
	"catalog/updateNavigation",
	"catalog/updateProps",
	"elements/fragment/create",
	"elements/fragment/edit",
	"elements/fragment/remove",
	"item/remove",
	"item/setPermission",
	"item/updateProps",
	"storage/startRecover",
	"versionControl/discard",
	"versionControl/branch/mergeInto",
	"versionControl/mergeConflict/abort",
	"versionControl/mergeConflict/resolve",
	"workspace/switch",
]);

const INVALIDATING_BRANCH_CALLERS = new Set([
	OnBranchUpdateCaller.Checkout,
	OnBranchUpdateCaller.CheckoutToNewCreatedBranch,
	OnBranchUpdateCaller.DiscardNoReset,
]);

export default class ClientCache {
	private readonly _zones: Record<ClientCacheZone, LRUCache<string, CacheEntry>>;
	private readonly _listeners = new Set<() => void>();
	private _revision = 0;

	constructor(limits: Partial<ClientCacheLimits> = {}, ttls: Partial<ClientCacheTtls> = {}) {
		const max = { ...DEFAULT_LIMITS, ...limits };
		const ttl = { ...DEFAULT_TTLS, ...ttls };
		this._zones = {
			[ClientCacheZone.ViewRenderData]: createZone(
				ClientCacheZone.ViewRenderData,
				max.viewRenderData,
				ttl.viewRenderData,
			),
			[ClientCacheZone.CatalogLogo]: createZone(ClientCacheZone.CatalogLogo, max.catalogLogo, ttl.catalogLogo),
		};
	}

	get<T>(zone: ClientCacheZone, key: string): T | undefined {
		const entry = this._zones[zone].get(key);
		return entry?.state === "resolved" ? (entry.value as T) : undefined;
	}

	getOrCreate<T>(zone: ClientCacheZone, key: string, load: () => Promise<T>): Promise<T> {
		const cache = this._zones[zone];
		const cached = cache.get(key);
		if (cached)
			return cached.state === "pending" ? (cached.value as Promise<T>) : Promise.resolve(cached.value as T);

		let request: Promise<T>;
		try {
			request = Promise.resolve(load());
		} catch (error) {
			return Promise.reject(error);
		}

		const pending: CacheEntry = { state: "pending", value: request };
		cache.set(key, pending);
		void request.then(
			(value) => {
				if (cache.peek(key) === pending) cache.set(key, { state: "resolved", value });
				else RELEASE_VALUE[zone]?.(value);
			},
			() => {
				if (cache.peek(key) === pending) cache.delete(key);
			},
		);
		return request;
	}

	clear(zone?: ClientCacheZone): void {
		if (zone) {
			if (!this._zones[zone].size) return;
			this._zones[zone].clear();
			this._notify();
			return;
		}

		const caches = Object.values(this._zones);
		if (!caches.some((cache) => cache.size)) return;
		for (const cache of caches) cache.clear();
		this._notify();
	}

	subscribe = (listener: () => void): (() => void) => {
		this._listeners.add(listener);
		return () => this._listeners.delete(listener);
	};

	getRevision = (): number => this._revision;

	private _notify(): void {
		this._revision++;
		for (const listener of this._listeners) listener();
	}
}

export const clientCache = new ClientCache();

const invalidateClientCache = ({ command }: OnDidCommandEv): void => {
	if (INVALIDATING_COMMANDS.has(command)) clientCache.clear();
};

const invalidateAfterSync = ({ syncData }: { syncData: { isVersionChanged: boolean } }): void => {
	if (syncData.isVersionChanged) clientCache.clear();
};

class ClientCacheInvalidationService implements ContextService {
	Init({ children }: { children: ReactElement }): ReactElement {
		useApiEvent("on-did-command", invalidateClientCache);
		useEffect(() => {
			let currentBranch = BranchUpdaterService.branch;
			const invalidateAfterBranchUpdate = (branch: typeof currentBranch, caller: OnBranchUpdateCaller) => {
				const previousBranch = currentBranch;
				currentBranch = branch;
				if (!INVALIDATING_BRANCH_CALLERS.has(caller)) return;

				const branchChanged =
					!previousBranch ||
					previousBranch.name !== branch.name ||
					previousBranch.lastCommitOid !== branch.lastCommitOid;
				if (caller === OnBranchUpdateCaller.DiscardNoReset || branchChanged) {
					clientCache.clear();
				}
			};
			BranchUpdaterService.addListener(invalidateAfterBranchUpdate);
			const syncFinished = SyncService.events.on("finish", invalidateAfterSync);
			const syncConflictResolved = SyncService.events.on("conflict-resolved", () => clientCache.clear());
			const syncConflictAborted = SyncService.events.on("conflict-aborted", () => clientCache.clear());

			return () => {
				BranchUpdaterService.removeListener(invalidateAfterBranchUpdate);
				SyncService.events.off(syncFinished);
				SyncService.events.off(syncConflictResolved);
				SyncService.events.off(syncConflictAborted);
			};
		}, []);
		return children;
	}
}

export const clientCacheInvalidationService = new ClientCacheInvalidationService();
